import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import jsPDF from "jspdf";
import { toJpeg } from "html-to-image";
import { getPagePx, paginateRows, NO_INVOICE_HTML } from "./pagination";
import FormFieldSet from "../FormFields/FormFieldSet";
import Button from "../Buttons/Button";
import Backdrop from "../Backgrounds/Backdrop";

/* Utility Functions */

const measureHeights = (refs) => ({
  headerFirstH: refs.headerFirstRef.current?.offsetHeight || 60,
  headerOtherH: refs.headerOtherRef.current?.offsetHeight || 30,
  footerFirstH: refs.footerFirstRef.current?.offsetHeight || 80,
  footerOtherH: refs.footerOtherRef.current?.offsetHeight || 40,
  contentTopH: refs.contentTopRef.current?.offsetHeight || 0,
  contentBottomH: refs.contentBottomRef.current?.offsetHeight || 40,
});

// Infer column count from Tailwind class like `grid-cols-9` or `grid-cols-[1fr_2fr_1fr]`
const inferGridColumns = (className = "", fallback = 2) => {
  if (!className) return fallback;
  const m = className.match(/grid-cols-(\d+)/);
  if (m) return parseInt(m[1], 10);
  const m2 = className.match(/grid-cols-\[(.+?)\]/);
  if (m2) {
    const tokens = m2[1].trim().split(/[ _]+/).filter(Boolean);
    return Math.max(1, tokens.length);
  }
  return fallback;
};

const NoInvoiceDetails = () => (
  <div className="measure-row py-8 text-center text-slate-500">
    <div className="text-lg font-medium">No Invoice Details</div>
    <div className="text-sm mt-2">
      Please provide a rowRenderer to display invoice items
    </div>
  </div>
);

const splitIntoPages = (
  measureRef,
  pageSize,
  layout,
  refs,
  setPages,
  setIsInitialized,
  bodyLayout = "list",
  gridColumns = 2,
  gridGap = 8,
  gridClassName = ""
) => {
  const measureEl = measureRef.current;
  if (!measureEl) return;

  const { height: pageH } = getPagePx(pageSize, layout);
  const heights = measureHeights(refs);

  const children = Array.from(measureEl.querySelectorAll(".measure-row"));
  if (!children.length) {
    setPages([[NO_INVOICE_HTML]]);
    setIsInitialized(true);
    return;
  }

  const items = children.map((el) => ({
    height: el.offsetHeight,
    html: el.outerHTML,
  }));

  let rowsForPagination = items;
  if (bodyLayout === "grid") {
    rowsForPagination = [];
    const cols = inferGridColumns(gridClassName, gridColumns);
    const wrappers = [];
    for (let i = 0; i < items.length; i += cols) {
      const group = items.slice(i, i + cols);
      const wrapper = document.createElement("div");
      const needsGrid = gridClassName && !/\bgrid\b/.test(gridClassName);
      wrapper.className = `measure-row ${
        needsGrid ? "grid " : ""
      }${gridClassName}`.trim();
      if (!gridClassName) {
        wrapper.style.display = "grid";
        wrapper.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
        wrapper.style.gap = `${gridGap}px`;
      }
      group.forEach((g) => {
        const cell = document.createElement("div");
        cell.innerHTML = g.html;
        wrapper.appendChild(cell);
      });
      measureEl.appendChild(wrapper);
      const rowH = wrapper.offsetHeight;
      const rowHtml = wrapper.outerHTML;
      rowsForPagination.push({ height: rowH, html: rowHtml });
      wrappers.push(wrapper);
    }
    // cleanup temporary measurement wrappers
    wrappers.forEach((w) => {
      try {
        measureEl.removeChild(w);
      } catch (_) {}
    });
  }

  const newPages = paginateRows(rowsForPagination, pageH, heights);
  setPages(newPages);
  setIsInitialized(true);
};

/* Main component for invoice generation */
const PdfGenerator = ({
  data = null,
  initialPageSize = "a4",
  initialLayout = "portrait",
  rows = null,
  items = [],
  rowRenderer = null,
  header = null,
  headerTwo = null,
  contentTop = null,
  contentBottom = null,
  footer = null,
  footerTwo = null,
  tableHeader = null,
  backgroundLogo = null,
  fileName = null,
  bodyLayout = "list",
  gridColumns = 2,
  gridGap = 8,
  gridClassName = "",
}) => {
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [layout, setLayout] = useState(initialLayout);
  const [pages, setPages] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const measureRef = useRef(null);
  const headerFirstRef = useRef(null);
  const headerOtherRef = useRef(null);
  const footerFirstRef = useRef(null);
  const footerOtherRef = useRef(null);
  const contentTopRef = useRef(null);
  const contentBottomRef = useRef(null);
  const splitTimerRef = useRef(0);
  const roRef = useRef(null);

  // Create refs object for utility functions
  const refs = {
    headerFirstRef,
    headerOtherRef,
    footerFirstRef,
    footerOtherRef,
    contentTopRef,
    contentBottomRef,
  };

  const bodyItems = useMemo(() => {
    if (!rowRenderer) return [];
    if (rows) return rows;
    if (items) return items;
    return [];
  }, [rows, items, rowRenderer]);

  const renderRow = rowRenderer || (() => <NoInvoiceDetails />);

  const handleSplitIntoPages = useCallback(() => {
    splitIntoPages(
      measureRef,
      pageSize,
      layout,
      refs,
      setPages,
      setIsInitialized,
      bodyLayout,
      gridColumns,
      gridGap,
      gridClassName
    );
  }, [pageSize, layout, bodyLayout, gridColumns, gridGap, gridClassName]);

  const debouncedSplit = useCallback(() => {
    clearTimeout(splitTimerRef.current);
    splitTimerRef.current = setTimeout(handleSplitIntoPages, 160);
  }, [handleSplitIntoPages]);

  const handlePdfDownload = useCallback(async () => {
    if (!pages.length) {
      alert("No pages available. Click Re-split first.");
      return;
    }
    setIsDownloading(true);
    try {
      const orientation = layout === "landscape" ? "l" : "p";
      const pdf = new jsPDF(orientation, "mm", pageSize);
      const { width: pagePxWidth, height: pagePxHeight } = getPagePx(
        pageSize,
        layout
      );

      for (let p = 0; p < pages.length; p++) {
        const node = document.getElementById(`pdf-page-${p}`);
        if (!node) continue;

        try {
          const imgs = Array.from(node.querySelectorAll("img"));
          await Promise.all(
            imgs.map(async (img) => {
              const src = img.getAttribute("src") || "";
              if (!src.trim()) {
                img.remove();
                return;
              }
              let isCross = false;
              try {
                const u = new URL(src, window.location.href);
                isCross = u.origin !== window.location.origin;
              } catch (_) {}
              if (isCross && !src.startsWith("data:")) {
                try {
                  const res = await fetch(src, { mode: "cors", credentials: "omit" });
                  if (!res.ok) throw new Error("fetch failed");
                  const blob = await res.blob();
                  await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      img.setAttribute("src", reader.result);
                      img.setAttribute("crossorigin", "anonymous");
                      resolve();
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  });
                } catch (_) {
                  img.remove();
                }
              }
            })
          );
        } catch (_) {}

        let imgData;
        try {
          imgData = await toJpeg(node, {
            quality: 1.0,
            backgroundColor: "#ffffff",
            width: pagePxWidth,
            height: pagePxHeight,
            skipFonts: true,
            pixelRatio: Math.max(2, window.devicePixelRatio || 1),
            cacheBust: true,
            style: { margin: 0, boxShadow: "none" },
          });
        } catch (_) {
          Array.from(node.querySelectorAll("img")).forEach((img) => img.remove());
          imgData = await toJpeg(node, {
            quality: 1.0,
            backgroundColor: "#ffffff",
            width: pagePxWidth,
            height: pagePxHeight,
            skipFonts: true,
            pixelRatio: Math.max(2, window.devicePixelRatio || 1),
            cacheBust: true,
            style: { margin: 0, boxShadow: "none" },
          });
        }

        const w = pdf.internal.pageSize.getWidth();
        const h = pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData, "JPEG", 0, 0, w, h, undefined, "FAST");
        if (p < pages.length - 1) pdf.addPage();
      }
      const finalName =
        fileName ||
        `invoice_${
          data?.invoiceNumber || data?.invoiceDeta?.invoiceNumber || "00001"
        }_${Date.now()}.pdf`;
      pdf.save(finalName);
    } catch (err) {
      console.log("Failed to generate PDF. Missing or cross-origin images were skipped.", err);
    } finally {
      setIsDownloading(false);
    }
  }, [pages, layout, pageSize, data, fileName]);

  useEffect(() => {
    const el = measureRef.current;
    if (!el) {
      debouncedSplit();
      return;
    }

    if (roRef.current) roRef.current.disconnect();
    const ro = new ResizeObserver(() => {
      if (isInitialized) debouncedSplit();
    });
    roRef.current = ro;

    ro.observe(el);
    [headerFirstRef, headerOtherRef, footerFirstRef, footerOtherRef].forEach(
      (r) => r.current && ro.observe(r.current)
    );
    const t = setTimeout(handleSplitIntoPages, 250);
    return () => {
      ro.disconnect();
      clearTimeout(t);
      clearTimeout(splitTimerRef.current);
    };
  }, [pageSize, layout]);

  useEffect(() => {
    if (isInitialized) debouncedSplit();
  }, [bodyItems.length]);

  const { width: pagePxWidth, height: pagePxHeight } = getPagePx(
    pageSize,
    layout
  );

  return (
    <div className="overflow-hidden h-full flex-1 flex flex-col pt-2 px-1 select-none">
      <Backdrop isOpen={isDownloading}>
        <div className="w-16 h-16 border-[5px] border-gray-800 border-b-primary rounded-full animate-spin" />
      </Backdrop>
      <div className="inputShadow flex gap-3 items-center justify-between flex-wrap bg-white p-2 rounded">
        <div className="flex gap-3 items-center justify-center">
          <div className="w-32">
            <FormFieldSet
              type="select"
              title="Page Size"
              noTitle
              value={pageSize}
              onChange={(value) => setPageSize(value)}
              options={[
                { label: "A4", value: "a4" },
                { label: "A5", value: "a5" },
                { label: "Letter", value: "letter" },
                { label: "Legal", value: "legal" },
              ]}
            />
          </div>

          <div className="w-32">
            <FormFieldSet
              type="select"
              title="Layout"
              noTitle
              value={layout}
              onChange={(value) => setLayout(value)}
              options={[
                { label: "Portrait", value: "portrait" },
                { label: "Landscape", value: "landscape" },
              ]}
            />
          </div>
          <Button
            title="Re-split Pages"
            onClick={handleSplitIntoPages}
            variant="outlined"
          />
        </div>
        <div>
          <Button title="Download PDF" onClick={handlePdfDownload} />
        </div>
      </div>

      {/* Hidden measurement container */}
      <div
        ref={measureRef}
        style={{
          position: "absolute",
          left: -9999,
          top: -9999,
          width: `${pagePxWidth}px`,
          visibility: "hidden",
        }}
      >
        {/* Header components for measurement */}
        <div ref={headerFirstRef}>
          {typeof header === "function" ? header() : header}
        </div>
        <div ref={headerOtherRef}>
          {typeof headerTwo === "function" ? headerTwo() : headerTwo}
        </div>

        {/* Footer components for measurement */}
        <div ref={footerFirstRef}>
          {typeof footer === "function" ? footer() : footer}
        </div>
        <div ref={footerOtherRef}>
          {typeof footerTwo === "function" ? footerTwo() : footerTwo}
        </div>

        {/* Content components for measurement */}
        <div ref={contentTopRef}>
          {typeof contentTop === "function" ? contentTop() : contentTop}
        </div>
        <div ref={contentBottomRef}>
          {typeof contentBottom === "function"
            ? contentBottom()
            : contentBottom}
        </div>

        {/* Body items for measurement */}
        <div>
          {bodyItems.length > 0 ? (
            bodyItems.map((it, idx) => renderRow(it, idx))
          ) : (
            <NoInvoiceDetails />
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="flex-1 overflow-auto bg-neutral-400 h-full p-5">
        {pages.length === 0 ? (
          <div className="text-center text-slate-500 p-12 bg-white rounded">
            <div className="text-lg font-medium mb-2">
              {isInitialized ? "No content to display" : "Loading preview..."}
            </div>
            <div className="text-sm">
              {!isInitialized && "Calculating page breaks..."}
            </div>
          </div>
        ) : (
          <div>
            {/* Pages with invoice items */}
            {pages.map((pageContent, pIndex) => {
              const { headerFirstH, headerOtherH, footerFirstH, footerOtherH } =
                measureHeights(refs);
              const isFirst = pIndex === 0;
              const isLast = pIndex === pages.length - 1;
              const headerH = isFirst ? headerFirstH : headerOtherH;
              const footerH = isLast ? footerFirstH : footerOtherH;
              const usableBodyH = Math.max(0, pagePxHeight - headerH - footerH);

              return (
                <div
                  key={pIndex}
                  id={`pdf-page-${pIndex}`}
                  className="bg-white mx-auto mb-10 shadow-md"
                  style={{
                    width: `${pagePxWidth}px`,
                    height: `${pagePxHeight}px`,
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                    position: "relative",
                  }}
                >
                  {/* Background Logo Overlay */}
                  {backgroundLogo && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 0,
                        opacity: 0.2,
                        pointerEvents: "none",
                      }}
                    >
                      <img
                        src={backgroundLogo}
                        alt="Background Logo"
                        style={{
                          maxWidth: `${pagePxWidth * 0.6}px`,
                          maxHeight: `${pagePxHeight * 0.6}px`,
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  )}

                  {/* Header Section */}
                  <div
                    className="pb-0 border-b border-slate-100"
                    style={{ position: "relative", zIndex: 1 }}
                  >
                    {isFirst
                      ? typeof header === "function"
                        ? header({
                            currentPage: pIndex + 1,
                            totalPages: pages.length,
                          })
                        : header
                      : typeof headerTwo === "function"
                      ? headerTwo({
                          currentPage: pIndex + 1,
                          totalPages: pages.length,
                        })
                      : headerTwo}
                  </div>

                  {/* Content Section */}
                  <div
                    className="flex-1 px-5"
                    style={{
                      overflow: "hidden",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {/* Content Top - appears only on first page */}
                    {isFirst && contentTop && (
                      <div className="mb-4">
                        {typeof contentTop === "function"
                          ? contentTop({
                              currentPage: pIndex + 1,
                              totalPages: pages.length,
                            })
                          : contentTop}
                      </div>
                    )}

                    {/* Table Header - appears on every page with invoice items */}
                    {tableHeader && (
                      <div className="mt-2">
                        {typeof tableHeader === "function"
                          ? tableHeader({
                              currentPage: pIndex + 1,
                              totalPages: pages.length,
                            })
                          : tableHeader}
                      </div>
                    )}

                    {/* Invoice items */}
                    <div
                      style={{
                        height: isFirst || isLast ? "auto" : `${usableBodyH}px`,
                        overflow: "hidden",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: pageContent.join(""),
                        }}
                      />
                    </div>

                    {/* Content Bottom - appears only on last page */}
                    {isLast && contentBottom && (
                      <div className="mt-4">
                        {typeof contentBottom === "function"
                          ? contentBottom({
                              currentPage: pIndex + 1,
                              totalPages: pages.length,
                            })
                          : contentBottom}
                      </div>
                    )}
                  </div>

                  {/* Footer Section */}
                  <div
                    className=" border-t border-slate-100"
                    style={{ position: "relative", zIndex: 1 }}
                  >
                    {isLast
                      ? typeof footer === "function"
                        ? footer({
                            currentPage: pIndex + 1,
                            totalPages: pages.length,
                          })
                        : footer
                      : typeof footerTwo === "function"
                      ? footerTwo({
                          currentPage: pIndex + 1,
                          totalPages: pages.length,
                        })
                      : footerTwo}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfGenerator;
